import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ChevronRight } from "lucide-react";
import { getTopic } from "../lib/content";
import { getImageMeta } from "../lib/images";
import { Reveal, useSEO } from "../components/site/Reveal";
import Newsletter from "../components/site/Newsletter";

const slugify = (s) => s.toLowerCase().replace(/[^\w]+/g, "-").replace(/^-|-$/g, "");

export default function TopicPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const topic = getTopic(slug);

  useSEO(topic?.title, topic?.metaDescription, topic?.hero);

  if (!topic) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="font-serif text-4xl text-ink mb-4">Topic not found</h1>
        <button onClick={() => navigate("/")} className="text-gold font-semibold">
          Return home
        </button>
      </div>
    );
  }

  const heroMeta = getImageMeta(topic.hero);

  return (
    <article>
      {/* HERO */}
      <header className={`relative flex items-end grain-overlay bg-night ${topic.hero ? "min-h-[52vh]" : "min-h-[34vh]"}`}>
        <div className="absolute inset-0">
          {topic.hero ? (
            <>
              <img src={topic.hero} alt={heroMeta.alt || topic.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-night via-night/70 to-night/30" />
            </>
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-night via-forest/40 to-night" />
          )}
        </div>
        <div className="relative max-w-5xl mx-auto px-6 pb-14 w-full">
          <nav className="flex items-center gap-2 text-xs text-cream/70 mb-5">
            <Link to="/" className="hover:text-gold">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-gold-soft uppercase tracking-widest">{topic.categoryLabel}</span>
          </nav>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl text-cream leading-[1.05] max-w-3xl text-shadow-hero"
          >
            {topic.title}
          </motion.h1>
          {heroMeta.caption && (
            <p data-testid="topic-hero-caption" className="mt-4 text-xs sm:text-sm text-cream/60 italic max-w-2xl">
              {heroMeta.caption}
            </p>
          )}
        </div>
      </header>

      {/* BODY */}
      <div className="max-w-6xl mx-auto px-6 py-20 grid lg:grid-cols-12 gap-12">
        {/* TOC */}
        <aside className="hidden lg:block lg:col-span-3">
          <div className="sticky top-28">
            <p className="text-xs uppercase tracking-widest text-gold mb-4">On this page</p>
            <ul className="space-y-3 border-l border-parchment">
              {topic.sections.map((s) => (
                <li key={s.heading}>
                  <a
                    href={`#${slugify(s.heading)}`}
                    className="block pl-4 -ml-px border-l border-transparent hover:border-gold text-sm text-ink-muted hover:text-forest transition-colors"
                  >
                    {s.heading}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* Content */}
        <div className="lg:col-span-9 prose-yoruba max-w-none">
          <Reveal>
            <p className="text-xl md:text-2xl font-serif text-forest leading-relaxed mb-12 border-l-2 border-gold pl-6">
              {topic.intro}
            </p>
          </Reveal>

          {topic.sections.map((s, idx) => (
            <Reveal key={s.heading} delay={0.05}>
              <section id={slugify(s.heading)} className="scroll-mt-28 mb-4">
                <h2 className="font-serif text-3xl md:text-4xl text-ink">{s.heading}</h2>
                {s.body.map((p, i) => (
                  <p key={i} className="text-lg text-ink-muted">{p}</p>
                ))}
                {s.list && (
                  <ul className="list-none space-y-3 mt-2">
                    {s.list.map((li, i) => (
                      <li key={i} className="flex gap-3 text-lg text-ink-muted">
                        <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-gold shrink-0" />
                        <span>{li}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </Reveal>
          ))}

          {/* Keywords */}
          {topic.keywords && (
            <div className="mt-12 pt-8 border-t border-parchment flex flex-wrap gap-2">
              {topic.keywords.map((k) => (
                <span key={k} className="text-xs bg-sand text-ink-muted rounded-full px-3 py-1.5 border border-parchment">
                  {k}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* RELATED */}
      {topic.related?.length > 0 && (
        <section className="bg-sand border-y border-parchment">
          <div className="max-w-6xl mx-auto px-6 py-20">
            <p className="uppercase tracking-[0.25em] text-xs text-gold mb-4">Continue Exploring</p>
            <h2 className="font-serif text-3xl md:text-4xl text-ink mb-10">Related Topics</h2>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {topic.related.map((rslug) => {
                const isSpecial = rslug === "history-timeline" || rslug === "orisa-encyclopedia";
                const to = rslug === "history-timeline" ? "/timeline" : rslug === "orisa-encyclopedia" ? "/orisa" : `/topic/${rslug}`;
                const r = getTopic(rslug);
                const label = r ? r.title : rslug === "history-timeline" ? "Yorùbá History Timeline" : "Òrìṣà Encyclopedia";
                return (
                  <Link
                    key={rslug}
                    to={to}
                    data-testid={`related-link-${rslug}`}
                    className="group flex items-center justify-between border border-parchment bg-cream rounded-xl px-5 py-5 hover:-translate-y-1 hover:border-gold transition-all"
                  >
                    <span className="font-serif text-lg text-ink group-hover:text-forest transition-colors pr-3">{label}</span>
                    <ArrowRight className="w-4 h-4 text-gold shrink-0" />
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <Newsletter />
    </article>
  );
}
